import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export type PresenterSlot = 'morning' | 'midday' | 'evening' | 'late';

export interface PresenterBriefStory {
  postId: string;
  slug: string | null;
  title: string;
  publishedAt: string | null;
  imageUrl: string | null;
  lead: string;
  summaryBullets: string[];
  echoSources: string[];
}

export interface PresenterBriefItem {
  id: string;
  locale: string;
  slot: PresenterSlot;
  lagosDate: string;
  generatedAt: string;
  groqModel: string;
  storyCount: number;
  content: {
    stories: Array<{ lead: string; summaryBullets: string[]; echoSources?: string[] }>;
    pronunciationGuide: Array<{ term: string; phonetic: string }>;
    themesToday: string;
  };
  stories: PresenterBriefStory[];
}

export interface PresenterBriefListResponse {
  config: {
    enabled: boolean;
    slotTimes: Record<PresenterSlot, { hour: number; minute: number }>;
  };
  items: PresenterBriefItem[];
}

export function usePresenterBriefs(params: {
  locale?: string;
  slot?: PresenterSlot;
  date?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['presenter-briefs', params],
    refetchInterval: 60_000,
    queryFn: () =>
      api.get<PresenterBriefListResponse>('/admin/presenter-briefs', {
        ...(params.locale ? { locale: params.locale } : {}),
        ...(params.slot ? { slot: params.slot } : {}),
        ...(params.date ? { date: params.date } : {}),
        ...(params.limit ? { limit: params.limit } : {}),
      }),
  });
}

export function useRegeneratePresenterBrief() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { locale: string; slot: PresenterSlot }) =>
      api.post<{ status: string; reason?: string; lagosDate: string }>(
        '/admin/presenter-briefs/regenerate',
        input,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['presenter-briefs'] }),
  });
}
